import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { FaTimes } from 'react-icons/fa';
import { Button, Input, Row } from 'reactstrap';
import {
    setValue
} from '../../../../components/InputField/helper';
import InputLabel from '../../../../components/InputField/InputLabel';
import { WrapperField } from '../../../../components/InputField/WrapperField';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import {
    calculateInitialErrors,
    collectVisibleFieldErrors,
    CONTROL_PRONG_OWNERSHIP_MESSAGE,
    getFieldErrorValue,
    getVisibleWizardFieldOrder,
    validateDuplicateContacts,
    V2_TYPE,
    WIZARD,
    WIZARD_NUM
} from './helperV2';
import { getIndividualShareholderOwnershipError } from '../helpers/ownershipValidation';
import Entity from './components/EntityV2';
import User from './components/User';
import ModernDisclosure from './components/ModernDisclosureV2';

const ADDRESS_TYPES = {
    REGISTERED: "REGISTERED",
    MAILING: "MAILING",
    PRINCIPAL: "PRINCIPAL",
};

const ADDRESS_LABELS = {
    REGISTERED: "Registered Address of Business",
    MAILING: "Mailing Address of Business",
    PRINCIPAL: "Principal Place of Business",
};

const DEFAULT_V2_EDIT_PERMISSIONS = {
    allowEntities: true,
    allowAdditionalIndividuals: true,
};

const compactAddressField = (fieldMap, fieldKey) => {
    const jsonField = fieldMap?.[fieldKey];
    return jsonField ? { ...jsonField, noLabel: true } : jsonField;
};

const contextualAddressField = (fieldMap, fieldKey, contextLabel) => {
    const jsonField = compactAddressField(fieldMap, fieldKey);
    if (!jsonField || !contextLabel) return jsonField;

    const baseLabel = typeof jsonField?.label === 'string' ? jsonField.label.trim() : '';
    return {
        ...jsonField,
        label: baseLabel ? `${baseLabel} for ${contextLabel}` : contextLabel,
    };
};

const contextualStreetField = (fieldMap, fieldKey, contextLabel) => {
    const jsonField = fieldMap?.[fieldKey];
    if (!jsonField) return jsonField;

    return {
        ...jsonField,
        noLabel: true,
        required: true,
        label: contextLabel ? `Street Name for ${contextLabel}` : 'Street Name',
    };
};

const parseArrayIndexFromPath = (resolvedFieldPath) => {
    const match = resolvedFieldPath?.match(/\[(\d+)\]/);
    return match ? Number(match[1]) : null;
};

const getPrimaryIndividualIndex = (data = {}) => {
    if (!Array.isArray(data?.individuals)) return -1;
    const idx = data.individuals.findIndex((ind) => ind?.isPrimaryUser);
    if (idx >= 0) return idx;
    return data.individuals.length ? 0 : -1;
};

const isBusinessFieldEditable = ({
    data,
    resolvedFieldPath,
    arrayIndexing,
    permissions = DEFAULT_V2_EDIT_PERMISSIONS,
}) => {
    if (!resolvedFieldPath) return true;

    const perms = {
        ...DEFAULT_V2_EDIT_PERMISSIONS,
        ...(permissions || {}),
    };

    if (resolvedFieldPath === 'hasEntity' || resolvedFieldPath.startsWith('entities')) {
        return perms.allowEntities;
    }

    if (resolvedFieldPath.startsWith('individuals')) {
        const idx = (typeof arrayIndexing === 'number' && !Number.isNaN(arrayIndexing))
            ? arrayIndexing
            : parseArrayIndexFromPath(resolvedFieldPath);

        const primaryIndex = getPrimaryIndividualIndex(data);
        const individual = idx !== null && idx !== undefined ? data?.individuals?.[idx] : null;
        if (
            idx === primaryIndex
            && (
                resolvedFieldPath.endsWith('.email')
                || resolvedFieldPath.endsWith('.isAuthorizedUser')
                || resolvedFieldPath.endsWith('.accessType')
            )
        ) {
            return false;
        }
        if (resolvedFieldPath.endsWith('.isControlPerson')) {
            return false;
        }
        if (idx !== primaryIndex && !perms.allowAdditionalIndividuals) {
            return false;
        }

        return true;
    }

    return true;
};

const enhanceErrorMessage = (field, errorMsg, data) => {
    const addressFieldLabels = {
        street: 'Street name',
        buildingNumber: 'Building and Number',
        city: 'City',
        state: 'State / Province / Region',
        pinCode: 'Postal / Zip Code',
        country: 'Country',
    };

    const isGenericAddressError = (message, addressField) => {
        if (!message || !addressField) return false;

        const normalizedMessage = String(message).trim();
        const label = addressFieldLabels[addressField];
        if (!label) return false;

        const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return new RegExp(`^Please enter(?: a valid)? ${escapedLabel}\\.?$`, 'i').test(normalizedMessage)
            || /^This field is required\.?$/i.test(normalizedMessage);
    };

    const buildAddressErrorMessage = (addressField, contextLabel, message) => {
        if (!addressField || !contextLabel) {
            return null;
        }

        if (addressField === 'buildingNumber' && isGenericAddressError(message, addressField)) {
            return `Please enter ${addressFieldLabels[addressField]} for ${contextLabel}.`;
        }

        return null;
    };

    // Handle nested array fields
    const match = field.match(/^(\w+)\[(\d+)\]\.(.+)$/);

    if (match) {
        const [, arrayName, index, nestedField] = match;
        const idx = parseInt(index);

        // Get context based on array type and nested field
        let context = '';

        if (arrayName === 'addressList') {
            const addressType = data?.addressList?.[idx]?.type;
            context = ADDRESS_LABELS[addressType] || 'Address';
        } else if (arrayName === 'entities') {
            context = `Entity ${idx + 1}`;
        } else if (arrayName === 'individuals') {
            const individual = data?.individuals?.[idx];
            context = individual?.isPrimaryUser ? 'Primary User' : `User ${idx + 1}`;
        }

        // Add field-specific context if needed
        if (nestedField.includes('address.')) {
            const addressField = nestedField.split('.').pop();
            const overrideMessage = buildAddressErrorMessage(addressField, context, errorMsg);
            if (overrideMessage) {
                return overrideMessage;
            }
            if (addressField === 'street' && isGenericAddressError(errorMsg, addressField)) {
                return `Please enter a valid street name for ${context}`;
            }
        }

        if (arrayName === 'addressList') {
            const overrideMessage = buildAddressErrorMessage(nestedField, context, errorMsg);
            if (overrideMessage) {
                return overrideMessage;
            }
        }

        return `${errorMsg} for ${context}`;
    }

    // Handle non-array fields
    const simpleArrayMatch = field.match(/^(\w+)\[(\d+)\]$/);
    if (simpleArrayMatch) {
        const [, arrayName, index] = simpleArrayMatch;
        const idx = parseInt(index);

        if (arrayName === 'entities') {
            return `${errorMsg} for Entity ${idx + 1}`;
        }
        if (arrayName === 'individuals') {
            const individual = data?.individuals?.[idx];
            const userLabel = individual?.isPrimaryUser ? 'Primary User' : `User ${idx + 1}`;
            return `${errorMsg} for ${userLabel}`;
        }
    }

    const principalAddressField = field.match(/^address\.(.+)$/);
    if (principalAddressField) {
        const [, addressField] = principalAddressField;
        const overrideMessage = buildAddressErrorMessage(addressField, 'Principal Place of Business', errorMsg);
        if (overrideMessage) {
            return overrideMessage;
        }
    }

    return errorMsg;
};

const extractFirstIndex = (path = "") => {
    const match = path.match(/\[(\d+)\]/);
    return match ? Number(match[1]) : null;
};

const buildLocalId = () =>
    `local_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

const ensureCurrentItemsHaveLocalIds = (currentItems = []) => {
    const nextCurrentItems = Array.isArray(currentItems) ? [...currentItems] : [];
    let changed = false;

    for (let index = 0; index < nextCurrentItems.length; index += 1) {
        const currentItem = nextCurrentItems[index];
        if (!_.isPlainObject(currentItem) || currentItem?._localId || currentItem?._id || currentItem?.aEId) {
            continue;
        }

        nextCurrentItems[index] = {
            ...currentItem,
            _localId: buildLocalId(),
        };
        changed = true;
    }

    return {
        currentItems: nextCurrentItems,
        changed,
    };
};

const ADDRESS_FIELDS_TO_CLONE = [
    'street',
    'buildingNumber',
    'city',
    'state',
    'stateProvince',
    'pinCode',
    'postalCode',
    'zipCode',
    'country',
];

const ADDED_BADGE_STYLE = {
    background: '#e6ffed',
    color: '#137333',
    border: '1px solid #b7eb8f',
    borderRadius: '8px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 600,
};

const REMOVED_BADGE_STYLE = {
    background: '#fff1f0',
    color: '#a8071a',
    border: '1px solid #ffa39e',
    borderRadius: '8px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 600,
};
const CONTROL_PERSON_BADGE_STYLE = {
    background: '#e8f4fd',
    color: '#0b5cab',
    border: '1px solid #9ec5fe',
    borderRadius: '8px',
    padding: '2px 8px',
    fontSize: '12px',
    fontWeight: 600,
};

// SYNC: Operator identity + diff helpers are duplicated in
// `caas-console/src/pages/Form/forms/BusinessPeriodicReview/BusinessInformation.js`.
const buildIndividualIdentity = (item = {}, index) => {
    const stable =
        item?.aEId ||
        item?.cyclosUserId ||
        item?.cyclosUserName ||
        item?._id;
    if (stable) return String(stable);

    if (item?._localId) return String(item._localId);

    if (item?.email) return `email:${String(item.email).trim().toLowerCase()}`;

    const name = [item?.firstName, item?.middleName, item?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();
    const dob = item?.dob ? String(item.dob).trim() : '';
    if (name || dob) return `name:${name.toLowerCase()}|dob:${dob}`;

    const mobilePhone = item?.mobilePhone ? String(item.mobilePhone).trim() : '';
    if (mobilePhone) {
        const code = item?.mobileCountryCode != null ? String(item.mobileCountryCode).trim() : '';
        return `mobile:${code}-${mobilePhone}`;
    }

    return `index:${index}`;
};

const buildEntityIdentity = (item = {}, index) => {
    const stable = item?.aEId || item?._id;
    if (stable) return String(stable);
    if (item?.name) return `name:${String(item.name).trim().toLowerCase()}`;
    return `index:${index}`;
};

const buildIndividualRenderKey = (item = {}, index) => item?._localId || buildIndividualIdentity(item, index);

const buildEntityRenderKey = (item = {}, index) => item?._localId || buildEntityIdentity(item, index);

const getIndividualKey = (item = {}) => {
    return (
        item?.aEId ||
        item?.cyclosUserId ||
        item?.cyclosUserName ||
        item?._id ||
        item?._localId ||
        item?.email ||
        null
    );
};

const getEntityKey = (item = {}) => {
    return (
        item?.aEId ||
        item?._id ||
        item?._localId ||
        item?.name ||
        null
    );
};

const buildReviewArrayChangeSet = (previousList = [], currentList = []) => {
    const addedIndices = new Set();
    currentList.forEach((item, idx) => {
        if (!item?.aEId) {
            addedIndices.add(idx);
        }
    });

    const currentAeIds = new Set(
        currentList
            .map((item) => item?.aEId)
            .filter(Boolean)
            .map((value) => String(value))
    );

    const removedItems = previousList.filter((item) => {
        if (!item?.aEId) return false;
        return !currentAeIds.has(String(item.aEId));
    });

    return { addedIndices, removedItems };
};

// Reconstruct original values by applying per-object diffMap entries.
const restoreOriginalFromDiffMap = (value) => {
    if (Array.isArray(value)) {
        return value.map((item) => restoreOriginalFromDiffMap(item));
    }
    if (!_.isPlainObject(value)) {
        return value;
    }

    const next = {};
    Object.entries(value).forEach(([key, child]) => {
        if (key === 'diffMap') return;
        next[key] = restoreOriginalFromDiffMap(child);
    });

    if (_.isPlainObject(value.diffMap)) {
        Object.entries(value.diffMap).forEach(([key, child]) => {
            next[key] = restoreOriginalFromDiffMap(child);
        });
    }

    return next;
};

const ensureArrayDiffBaseline = (dataObj, arrayKey) => {
    if (!dataObj || dataObj?.diffMap?.[arrayKey]) return dataObj;
    const currentList = Array.isArray(dataObj?.[arrayKey]) ? dataObj[arrayKey] : [];
    const baselineList = currentList.map((item) => restoreOriginalFromDiffMap(item));
    return {
        ...dataObj,
        diffMap: {
            ...(dataObj.diffMap || {}),
            [arrayKey]: baselineList,
        },
    };
};

const buildIndividualTitle = (item = {}, index) => {
    const name = [item?.firstName, item?.middleName, item?.lastName].filter(Boolean).join(' ');
    if (name) return `Removed User — ${name}`;
    if (item?.email) return `Removed User — ${item.email}`;
    return `Removed User #${index + 1}`;
};

const buildCompactListSummary = (items = [], formatter, maxItems = 2) => {
    const list = (items || []).map((item, idx) => formatter?.(item, idx)).filter(Boolean);
    if (!list.length) return '';
    if (list.length <= maxItems) return list.join(' | ');
    return `${list.slice(0, maxItems).join(' | ')} | +${list.length - maxItems} more`;
};

export const LabelValue = ({ label = "", value = "" }) => (
    <div className='d-flex gap-1 mb-1 text-muted'>
        <div className='fw-semibold fs-6'>{label}:</div>
        <div className='fs-6'>{value}</div>
    </div>
)

const BTN = ({ onClick, label, disabled }) => {
    const btnStyle = {
        backgroundColor: '#fff',
        borderRadius: "10px",
        border: "1px solid #1678AE",
        color: '#1678AE',
        whiteSpace: 'normal',
        textAlign: 'left',
        maxWidth: '100%',
    };
    return (
        <div>
            <Button
                className='mb-3 mt-1'
                disabled={disabled}
                style={btnStyle}
                onClick={onClick}
            >
                {label}
            </Button>
        </div>
    );
};

const BusinessInformation = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    handlePrev,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    isForEDD,
    handleChange,
    setData,
    applicationForm,
    maxAuthorizedUsers = 11,
    pageNum = WIZARD_NUM.BusinessInformation,
}) => {
    const { showError } = useContext(DialogContext);
    const supportsControlPersonFlow = [V2_TYPE.PLUS, V2_TYPE.PRIME, V2_TYPE.UPGRADE].includes(Number(applicationForm?.type))
        && Boolean(cyclosFields?.["individuals[].isControlPerson"]);
    // Broker-managed customers are restricted to a single authorized user.
    const effectiveMaxAuthorizedUsers = applicationForm?.brokerId ? 1 : maxAuthorizedUsers;
    const permissions = DEFAULT_V2_EDIT_PERMISSIONS;
    const showChangeTracking = Boolean(isForEDD);
    const individuals = useMemo(() => (Array.isArray(data?.individuals) ? data.individuals : []), [data?.individuals]);
    const { addedIndices: addedIndividualIndices, removedItems: removedIndividuals } = useMemo(() => {
        if (!showChangeTracking || !Array.isArray(data?.diffMap?.individuals)) {
            return { addedIndices: new Set(), removedItems: [] };
        }
        const previousIndividuals = data.diffMap.individuals;
        return buildReviewArrayChangeSet(previousIndividuals, individuals);
    }, [data?.diffMap?.individuals, individuals, showChangeTracking]);
    const { addedIndices: addedEntityIndices, removedItems: removedEntities } = useMemo(() => {
        if (!showChangeTracking || !Array.isArray(data?.diffMap?.entities)) {
            return { addedIndices: new Set(), removedItems: [] };
        }
        const previousEntities = data.diffMap.entities;
        const currentEntities = Array.isArray(data?.entities) ? data.entities : [];
        return buildReviewArrayChangeSet(previousEntities, currentEntities);
    }, [data?.diffMap?.entities, data?.entities, showChangeTracking]);
    const primaryUserIndex = getPrimaryIndividualIndex(data);
    const effectivePrimaryIndex = primaryUserIndex >= 0 ? primaryUserIndex : (individuals.length ? 0 : -1);

    const canEditEntities = !asLabel && permissions.allowEntities;

    const evaluateFieldEditable = useCallback(
        (fieldPath, { arrayIndexing = null } = {}) =>
            isBusinessFieldEditable({
                data,
                resolvedFieldPath: fieldPath,
                arrayIndexing,
                permissions,
            }),
        [data, permissions]
    );

    const noop = useCallback(() => { }, []);

    const canEditIndividual = (idx) => {
        // if (asLabel) return false;
        if (idx === effectivePrimaryIndex) {
            return true;
        }
        return permissions.allowAdditionalIndividuals;
    };

    const normalizeLockedIndividual = useCallback((individual, idx) => {
        if (!_.isPlainObject(individual)) {
            return individual;
        }

        const nextIndividual = { ...(individual ?? {}) };
        nextIndividual.isControlPerson = supportsControlPersonFlow && nextIndividual?.isControlPerson === true;

        if (idx === effectivePrimaryIndex) {
            nextIndividual.isPrimaryUser = true;
            nextIndividual.isAuthorizedUser = true;
            if (!nextIndividual.email && data?.email) {
                nextIndividual.email = data.email;
            }
            if (!nextIndividual.accessType) {
                nextIndividual.accessType = "NoGroup";
            }
        } else if (nextIndividual.isAuthorizedUser && !nextIndividual.accessType) {
            nextIndividual.accessType = "NoGroup";
        }

        return nextIndividual;
    }, [data?.email, effectivePrimaryIndex, supportsControlPersonFlow]);

    const normalizeLockedIndividuals = useCallback(
        (items = []) => (Array.isArray(items) ? items : []).map((individual, idx) => normalizeLockedIndividual(individual, idx)),
        [normalizeLockedIndividual]
    );

    const clonePrincipalAddressIntoList = useCallback((state, type) => {
        let working = { ...state };
        const addressList = Array.isArray(working.addressList) ? [...working.addressList] : [];

        let index = addressList.findIndex(item => item?.type === type);
        if (index === -1) {
            const insertIndex = type === "REGISTERED" ? 0 : Math.min(1, addressList.length);
            addressList.splice(insertIndex, 0, { type });
            working = setValue(working, 'addressList', addressList, false);
            index = insertIndex;
        }

        const principalAddress = working.address || {};
        ADDRESS_FIELDS_TO_CLONE.forEach((field) => {
            const value = principalAddress[field];
            const resolved = value !== undefined && value !== null ? value : "";
            working = setValue(
                working,
                `addressList[].${field}`,
                resolved,
                isForEDD,
                "",
                index
            );
        });

        working = setValue(
            working,
            `addressList[].hidePinCode`,
            principalAddress.hidePinCode ?? false,
            false,
            "",
            index
        );

        return { state: working, index };
    }, [isForEDD]);

    const clearAddressErrors = useCallback((targetIndex) => {
        setErrors((prevErrors) => {
            const next = { ...prevErrors };
            [
                `addressList[${targetIndex}].street`,
                `addressList[${targetIndex}].buildingNumber`,
                `addressList[${targetIndex}].city`,
                `addressList[${targetIndex}].state`,
                `addressList[${targetIndex}].pinCode`,
                `addressList[${targetIndex}].country`,
            ].forEach((path) => {
                delete next[path];
            });
            return next;
        });
    }, [setErrors]);

    const shouldValidateField = useCallback(
        (field, { arrayIndexing, resolvedFieldPath, data: contextData }) =>
            isBusinessFieldEditable({
                data: contextData ?? data,
                resolvedFieldPath,
                arrayIndexing,
                permissions,
            }),
        [data, permissions]
    );

    // Utility function to deep clone address object with per-entry diffMap preservation
    const checkNext = () => {
        const visibleFieldOrder = getVisibleWizardFieldOrder({
            fields: iPlusFields,
            wizard,
            data,
        });
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors,
            type: Number(applicationForm?.type),
            shouldValidateField,
        });
        const mergedErrors = {
            ...(errors || {}),
            ...fieldErrors,
        };
        setErrors(mergedErrors);

        for (const resolvedFieldPath of visibleFieldOrder) {
            const arrayIndexing = extractFirstIndex(resolvedFieldPath);
            if (
                !shouldValidateField(null, {
                    arrayIndexing,
                    resolvedFieldPath,
                })
            ) {
                continue;
            }

            const fieldError = getFieldErrorValue(mergedErrors, resolvedFieldPath);
            if (fieldError) {
                setHasError(true);
                const enhancedError = enhanceErrorMessage(resolvedFieldPath, fieldError, data);
                showError(enhancedError || fieldError);
                return false;
            }
        }

        const duplicateContactErrors = validateDuplicateContacts({
            ...data,
            individuals: normalizeLockedIndividuals(individuals),
        });
        const duplicateContactFields = Object.keys(duplicateContactErrors || {});
        if (duplicateContactFields.length) {
            setErrors((prevErrors) => ({
                ...(prevErrors || {}),
                ...duplicateContactErrors,
            }));
            setHasError(true);
            const firstDuplicateField = duplicateContactFields[0];
            const firstDuplicateError = duplicateContactErrors[firstDuplicateField];
            const enhancedError = enhanceErrorMessage(firstDuplicateField, firstDuplicateError, data);
            showError(enhancedError || firstDuplicateError);
            return false;
        }

        if (permissions.allowAdditionalIndividuals) {
            const normalizedIndividuals = normalizeLockedIndividuals(individuals);
            const hasEligibleIndividual = normalizedIndividuals.some(
                (ind) => ind?.isShareHolder === true
                    || ind?.isAuthorizedUser === true
                    || ind?.isIndependentDirector === true
                    || (supportsControlPersonFlow && ind?.isControlPerson === true)
                    || ind?.isDirector === true
            );
            if (!hasEligibleIndividual) {
                showError(supportsControlPersonFlow
                    ? "At least one person must be either a shareholder, authorized user, director, or Control Prong"
                    : "At least one person must be either a shareholder, authorized user, or director");
                return false;
            }

            const individualOwnershipError = getIndividualShareholderOwnershipError(normalizedIndividuals);
            if (individualOwnershipError) {
                showError(individualOwnershipError);
                return false;
            }

            const hasControlProngOrQualifiedOwner = normalizedIndividuals.some((ind) => (
                (supportsControlPersonFlow && ind?.isControlPerson === true)
                || (ind?.isShareHolder === true && (parseFloat(ind?.percentOwner) || 0) >= 25)
            ));
            if (!hasControlProngOrQualifiedOwner) {
                showError(CONTROL_PRONG_OWNERSHIP_MESSAGE);
                return false;
            }

            const authorizedCount = normalizedIndividuals.filter((ind) => ind?.isAuthorizedUser).length;
            if (authorizedCount > effectiveMaxAuthorizedUsers) {
                showError(`Maximum ${effectiveMaxAuthorizedUsers} authorized user${effectiveMaxAuthorizedUsers === 1 ? "" : "s"} allowed. Currently: ${authorizedCount}`);
                return false;
            }

            for (let idx = 0; idx < normalizedIndividuals.length; idx += 1) {
                const person = normalizedIndividuals[idx];
                const userLabel = person?.isPrimaryUser ? 'Primary User' : `User ${idx + 1}`;

                if (!person) {
                    continue;
                }

                const isControlPerson = supportsControlPersonFlow && person?.isControlPerson === true;
                const isShareHolder = person?.isShareHolder === true;
                const isAuthorizedUser = person?.isAuthorizedUser === true;
                const isIndependentDirector = person?.isIndependentDirector === true;
                const isDirector = person?.isDirector === true;

                if (!isShareHolder && !isAuthorizedUser && !isIndependentDirector && !isControlPerson && !isDirector) {
                    showError(supportsControlPersonFlow
                        ? `${userLabel} should be either shareholder, authorized user, director, or Control Prong.`
                        : `${userLabel} should be either shareholder, authorized user, or director.`);
                    return false;
                }

                if (isShareHolder) {
                    if (person?.percentOwner === undefined || person?.percentOwner === null || person?.percentOwner === '') {
                        showError(`Please enter Percentage of Ownership for ${userLabel}`);
                        return false;
                    }
                }

                if (isShareHolder) {
                    if (!Array.isArray(person?.sourceOfWealthList) || !person?.sourceOfWealthList?.length) {
                        showError(`Please select at least one source of wealth for ${userLabel}`);
                        return false;
                    }
                }

                if (isShareHolder) {
                    const percentOwner = parseFloat(person?.percentOwner);
                    if (Number.isNaN(percentOwner)) {
                        showError(`Please enter a valid percentage of ownership for ${userLabel}`);
                        return false;
                    }
                    if (percentOwner <= 0) {
                        showError(`Percent ownership must be greater than 0% for ${userLabel}`);
                        return false;
                    }
                    if (percentOwner > 100) {
                        showError(`Percent ownership cannot exceed 100% for ${userLabel}`);
                        return false;
                    }
                }
            }
        }

        setErrors({});
        return true;
    };

    const handleSubmit = () => {
        const isFormFieldValid = checkNext();
        if (isFormFieldValid) {
            setHasError(false);
            const nextStep = applicationForm?.getNextStepForForm?.({
                wizard,
                applicationForm,
                data,
            }) || { wizard: WIZARD.BusinessActivity };
            return handleUpdate({ nextStep });
        }
    };

    const handleUpdateFormData = (updatedData) => {
        handleChange(updatedData);
    };

    const handleErrors = (field, errorMsg) => {
        setErrors((prevErrors) => {
            const currentValue = prevErrors?.[field];
            if (_.isEqual(currentValue, errorMsg)) {
                return prevErrors;
            }
            const updatedErrors = _.cloneDeep(prevErrors);
            updatedErrors[field] = errorMsg;
            return updatedErrors;
        });
    };

    const handleAddressSameChange = (type) => {
        if (type === ADDRESS_TYPES.REGISTERED) {
            setData(prev => {
                const currentVal = prev.isRegisteredAddressSame;
                const newVal = !currentVal;
                let updatedData = { ...prev };

                // Track isRegisteredAddressSame change with diffMap
                updatedData = setValue(updatedData, 'isRegisteredAddressSame', newVal, isForEDD);

                if (newVal && updatedData.address) {
                    const { state: syncedState, index } = clonePrincipalAddressIntoList(updatedData, ADDRESS_TYPES.REGISTERED);
                    updatedData = syncedState;
                    clearAddressErrors(index);
                }

                calculateInitialErrors({
                    data: updatedData,
                    step: { wizard: WIZARD.BusinessInformation },
                    iPlusFields,
                    setErrors,
                    errors,
                    shouldValidateField,
                });
                return updatedData;
            });
        } else if (type === ADDRESS_TYPES.MAILING) {
            setData(prev => {
                const currentVal = prev.isMailingAddressSame;
                const newVal = !currentVal;
                let updatedData = { ...prev };

                // Track isMailingAddressSame change with diffMap
                updatedData = setValue(updatedData, 'isMailingAddressSame', newVal, isForEDD);

                if (newVal && updatedData.address) {
                    const { state: syncedState, index } = clonePrincipalAddressIntoList(updatedData, ADDRESS_TYPES.MAILING);
                    updatedData = syncedState;
                    clearAddressErrors(index);
                }

                calculateInitialErrors({
                    data: updatedData,
                    step: { wizard: WIZARD.BusinessInformation },
                    iPlusFields,
                    setErrors,
                    errors,
                    shouldValidateField,
                });
                return updatedData;
            });
        }
    };

    const handleAddressChangeWithType = (type, updatedData) => {
        if (type === ADDRESS_TYPES.REGISTERED) {
            // When REGISTERED address is changed, uncheck the "same as principal" option
            updatedData = setValue(updatedData, 'isRegisteredAddressSame', false, isForEDD);
        } else if (type === ADDRESS_TYPES.MAILING) {
            // When MAILING address is changed, uncheck the "same as principal" option
            updatedData = setValue(updatedData, 'isMailingAddressSame', false, isForEDD);
        } else if (type === ADDRESS_TYPES.PRINCIPAL) {
            // When PRINCIPAL address is changed, uncheck both "same as principal" options
            updatedData = setValue(updatedData, 'isRegisteredAddressSame', false, isForEDD);
            updatedData = setValue(updatedData, 'isMailingAddressSame', false, isForEDD);
        }
        handleUpdateFormData(updatedData);
    };

    const AddEntity = () => {
        if (!canEditEntities) return;
        setData(prevItems => {
            let updatedData = isForEDD ? ensureArrayDiffBaseline(prevItems, 'entities') : { ...prevItems };
            const newItems = [...(updatedData?.entities || []), { _isNew: true, _localId: buildLocalId() }];
            updatedData = setValue(updatedData, 'entities', newItems, isForEDD);
            return updatedData;
        });
    };

    const removeEntity = (index) => {
        if (!canEditEntities) return;
        setData(prevItems => {
            let updatedData = isForEDD ? ensureArrayDiffBaseline(prevItems, 'entities') : { ...prevItems };
            const newItems = [...(updatedData?.entities || [])];
            newItems.splice(index, 1);
            updatedData = setValue(updatedData, 'entities', newItems, isForEDD);
            return updatedData;
        });
    };

    const reAddRemovedEntity = (removedEntity) => {
        if (asLabel || !permissions.allowEntities) return;
        setData(prevItems => {
            const current = Array.isArray(prevItems?.entities) ? [...prevItems.entities] : [];
            const removedKey = getEntityKey(removedEntity);
            const alreadyPresent = removedKey
                ? current.some((item) => getEntityKey(item) === removedKey)
                : current.some((item) => _.isEqual(item, removedEntity));
            if (alreadyPresent) {
                return prevItems;
            }
            const sanitized = { ...removedEntity };
            const hasStableId = Boolean(sanitized?.aEId || sanitized?._id);
            if (!hasStableId && !sanitized._localId) {
                sanitized._localId = buildLocalId();
            }
            const updatedItems = [...current, sanitized];
            let updatedData = { ...prevItems };
            updatedData = setValue(updatedData, 'hasEntity', true, isForEDD);
            updatedData = setValue(updatedData, 'entities', updatedItems, isForEDD);
            return updatedData;
        });
    };

    const AddUser = () => {
        if (asLabel || !permissions.allowAdditionalIndividuals) return;
        setData(prevItems => {
            let updatedData = isForEDD ? ensureArrayDiffBaseline(prevItems, 'individuals') : { ...prevItems };
            const newItems = [...(updatedData?.individuals || []), {
                isAuthorizedUser: true,
                accessType: "NoGroup",
                isShareHolder: false,
                isIndependentDirector: false,
                _isNew: true,
                _localId: buildLocalId(),
            }];
            updatedData = setValue(updatedData, 'individuals', newItems, isForEDD);
            return updatedData;
        });
    };

    const AddShareholder = () => {
        if (asLabel || !permissions.allowAdditionalIndividuals) return;
        setData(prevItems => {
            let updatedData = isForEDD ? ensureArrayDiffBaseline(prevItems, 'individuals') : { ...prevItems };
            const newItems = [...(updatedData?.individuals || []), {
                isShareHolder: true,
                isAuthorizedUser: false,
                isIndependentDirector: false,
                _isNew: true,
                _localId: buildLocalId(),
            }];
            updatedData = setValue(updatedData, 'individuals', newItems, isForEDD);
            return updatedData;
        });
    };

    const AddIndependentDirector = () => {
        if (asLabel || !permissions.allowAdditionalIndividuals) return;
        setData(prevItems => {
            let updatedData = isForEDD ? ensureArrayDiffBaseline(prevItems, 'individuals') : { ...prevItems };
            const newItems = [...(updatedData?.individuals || []), {
                isShareHolder: false,
                isAuthorizedUser: false,
                isIndependentDirector: true,
                _isNew: true,
                _localId: buildLocalId(),
            }];
            updatedData = setValue(updatedData, 'individuals', newItems, isForEDD);
            return updatedData;
        });
    };

    const AddControlPerson = () => {
        if (!supportsControlPersonFlow || asLabel || !permissions.allowAdditionalIndividuals) return;
        setData(prevItems => {
            let updatedData = isForEDD ? ensureArrayDiffBaseline(prevItems, 'individuals') : { ...prevItems };
            const newItems = [...(updatedData?.individuals || []), {
                isControlPerson: true,
                isAuthorizedUser: false,
                isShareHolder: false,
                isIndependentDirector: false,
                _isNew: true,
                _localId: buildLocalId(),
            }];
            updatedData = setValue(updatedData, 'individuals', newItems, isForEDD);
            return updatedData;
        });
    };

    const removeUser = (index) => {
        // When the page is disabled (banner state / read-only), do not allow destructive actions.
        if (asLabel) return;
        if (!canEditIndividual(index) || index === effectivePrimaryIndex) return;
        setData(prevItems => {
            let updatedData = isForEDD ? ensureArrayDiffBaseline(prevItems, 'individuals') : { ...prevItems };
            const newItems = [...(updatedData?.individuals || [])];
            newItems.splice(index, 1);
            updatedData = setValue(updatedData, 'individuals', newItems, isForEDD);
            return updatedData;
        });
    };

    const reAddRemovedUser = (removedUser) => {
        if (asLabel || !permissions.allowAdditionalIndividuals) return;
        setData(prevItems => {
            const current = Array.isArray(prevItems?.individuals) ? [...prevItems.individuals] : [];
            const removedKey = getIndividualKey(removedUser);
            const alreadyPresent = removedKey
                ? current.some((item) => getIndividualKey(item) === removedKey)
                : current.some((item) => _.isEqual(item, removedUser));
            if (alreadyPresent) {
                return prevItems;
            }
            const authorizedCount = current.filter((ind) => ind?.isAuthorizedUser).length;
            if (removedUser?.isAuthorizedUser && authorizedCount >= effectiveMaxAuthorizedUsers) {
                showError(`Maximum ${effectiveMaxAuthorizedUsers} authorized user${effectiveMaxAuthorizedUsers === 1 ? "" : "s"} allowed.`);
                return prevItems;
            }
            const hasPrimary = current.some((ind) => ind?.isPrimaryUser);
            const sanitized = { ...removedUser };
            const hasStableId = Boolean(sanitized?.aEId || sanitized?.cyclosUserId || sanitized?.cyclosUserName || sanitized?._id);
            if (!hasStableId && !sanitized._localId) {
                sanitized._localId = buildLocalId();
            }
            if (hasPrimary && sanitized.isPrimaryUser) {
                sanitized.isPrimaryUser = false;
            }
            const updatedItems = [...current, sanitized];
            let updatedData = { ...prevItems };
            updatedData = setValue(updatedData, 'individuals', updatedItems, isForEDD);
            return updatedData;
        });
    };


    useEffect(() => {
        if (data) {
            let needsUpdate = false;
            let updatedData = { ...data };

            // Ensure addressList is an array
            if (!Array.isArray(data.addressList)) {
                updatedData = setValue(updatedData, 'addressList', [], false);
                needsUpdate = true;
            }

            // Filter addressList to only include REGISTERED and MAILING addresses
            const addressTypeToInclude = [ADDRESS_TYPES.REGISTERED, ADDRESS_TYPES.MAILING];
            const originalLength = updatedData.addressList.length;
            const filteredAddressList = updatedData.addressList.filter(addr =>
                addressTypeToInclude.includes(addr.type)
            );

            if (filteredAddressList.length !== originalLength) {
                updatedData = setValue(updatedData, 'addressList', filteredAddressList, false);
                needsUpdate = true;
            }

            // Ensure we have both REGISTERED and MAILING addresses
            const hasRegistered = updatedData.addressList.some(addr => addr.type === ADDRESS_TYPES.REGISTERED);
            const hasMailing = updatedData.addressList.some(addr => addr.type === ADDRESS_TYPES.MAILING);

            if (!hasRegistered) {
                const newAddressList = [...updatedData.addressList];
                newAddressList.splice(0, 0, { type: ADDRESS_TYPES.REGISTERED });
                updatedData = setValue(updatedData, 'addressList', newAddressList, false);
                needsUpdate = true;
            }

            if (!hasMailing) {
                const newAddressList = [...updatedData.addressList];
                const insertIndex = newAddressList.length > 0 ? 1 : 0;
                newAddressList.splice(insertIndex, 0, { type: ADDRESS_TYPES.MAILING });
                updatedData = setValue(updatedData, 'addressList', newAddressList, false);
                needsUpdate = true;
            }

            const currentEntities = Array.isArray(updatedData.entities) ? updatedData.entities : [];
            const syncedEntities = ensureCurrentItemsHaveLocalIds(currentEntities);
            if (!_.isEqual(currentEntities, syncedEntities.currentItems)) {
                updatedData = setValue(updatedData, 'entities', syncedEntities.currentItems, false);
                needsUpdate = true;
            }

            const currentIndividuals = Array.isArray(updatedData.individuals) ? updatedData.individuals : [];
            const syncedIndividuals = ensureCurrentItemsHaveLocalIds(currentIndividuals);
            const normalizedIndividuals = normalizeLockedIndividuals(syncedIndividuals.currentItems);
            if (!_.isEqual(currentIndividuals, normalizedIndividuals)) {
                updatedData = setValue(updatedData, 'individuals', normalizedIndividuals, false);
                needsUpdate = true;
            }

            if (needsUpdate) {
                setData(updatedData);
            }
        }
    }, [data, isForEDD, normalizeLockedIndividuals, setData]);

    const canEditEIN = evaluateFieldEditable('EIN');
    const canEditBusinessTaxId = evaluateFieldEditable('Business_Tax_ID');
    const canEditBusinessRegistrationCountry = evaluateFieldEditable('Business_Registration_Country');
    const canEditBusinessMobilePhone = evaluateFieldEditable('mobilePhone');
    const canEditBusinessLandlinePhone = evaluateFieldEditable('landlinePhone');

    return (
        <div className="row" data-testid="business-v2-business-information-step">
            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["Business_Name"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('Business_Name', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["Doing_Business_As"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('Doing_Business_As', errorMsg)}
                isForEDD={isForEDD}
            />

            <InputLabel className="mt-3" required label={"Address of Principal Place of Business (e.g. operating address)"} labelStyle={{ fontSize: 15 }} />
            <p className='ps-3 ms-0 mb-2' style={{ fontSize: 11, fontStyle: 'italic' }}>
                This is the address you operate your business at, not a registered address. Please do not enter P.O. Boxes, they will not be accepted.
            </p>

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["address.street"]}
                dataObj={data}
                haveDependentSubFields
                onChange={(updatedData) => handleAddressChangeWithType(ADDRESS_TYPES.PRINCIPAL, updatedData)}
                customProp={iPlusFields?.filter(field => field?.parentField === "address")}
                showError={hasError}
                onError={(errorMsg, streetNameLabel) => {
                    const label = streetNameLabel || "address.street";
                    handleErrors(label, errorMsg);
                }}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={contextualAddressField(cyclosFields, "address.buildingNumber", "Principal Place of Business")}
                dataObj={data}
                onChange={(updatedData) => handleAddressChangeWithType(ADDRESS_TYPES.PRINCIPAL, updatedData)}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.buildingNumber', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={compactAddressField(cyclosFields, "address.city")}
                dataObj={data}
                className={'col-12 col-sm-3'}
                onChange={(updatedData) => handleAddressChangeWithType(ADDRESS_TYPES.PRINCIPAL, updatedData)}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.city', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={compactAddressField(cyclosFields, "address.state")}
                dataObj={data}
                className={'col-12 col-sm-3'}
                onChange={(updatedData) => handleAddressChangeWithType(ADDRESS_TYPES.PRINCIPAL, updatedData)}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.state', errorMsg)}
                isForEDD={isForEDD}
            />

            {!data?.address?.hidePinCode && (
                <WrapperField
                    disabled={asLabel}
                    jsonField={compactAddressField(cyclosFields, "address.pinCode")}
                    dataObj={data}
                    className={'col-12 col-sm-3'}
                    onChange={(updatedData) => handleAddressChangeWithType(ADDRESS_TYPES.PRINCIPAL, updatedData)}
                    showError={hasError}
                    onError={(errorMsg) => handleErrors('address.pinCode', errorMsg)}
                    isForEDD={isForEDD}
                />
            )}

            <WrapperField
                disabled={asLabel}
                jsonField={compactAddressField(cyclosFields, "address.country")}
                dataObj={data}
                className={'col-12 col-sm-3 address-country-picker'}
                onChange={(updatedData) => handleAddressChangeWithType(ADDRESS_TYPES.PRINCIPAL, updatedData)}
                showError={hasError}
                onError={(errorMsg) => handleErrors('address.country', errorMsg)}
                isForEDD={isForEDD}
            />

            {data?.address?.hidePinCode && (
                <div className={'col-12 col-sm-3'} aria-hidden="true" />
            )}

            {/* Address List (REGISTERED and MAILING) */}
            {data?.addressList?.map((address, index) => {
                const checkboxId = `mailingCheckbox-${address?.type}-${index}`;

                return (
                    <div key={index} className='address-box-tab position-relative'>
                        <div className='d-flex align-items-center justify-content-between'>
                            <span style={{ fontSize: 16, fontWeight: "500" }}>
                                {ADDRESS_LABELS[address?.type] ?? ""}
                                <font color="red">*</font>
                            </span>

                            <div className="mb-0 ms-1 d-flex align-items-center">
                                <Input
                                    type="checkbox"
                                    id={checkboxId}
                                    checked={
                                        address?.type === ADDRESS_TYPES.MAILING
                                            ? data?.isMailingAddressSame
                                            : data?.isRegisteredAddressSame
                                    }
                                    onChange={() => handleAddressSameChange(address?.type)}
                                    disabled={asLabel}
                                />
                                <label htmlFor={checkboxId} className="ps-2 mt-2">
                                    Same as the principal place of business
                                </label>
                            </div>
                        </div>

                        <Row>
                            <WrapperField
                                disabled={asLabel}
                                jsonField={contextualStreetField(
                                    cyclosFields,
                                    "addressList[].street",
                                    ADDRESS_LABELS[address?.type] || "Address"
                                )}
                                arrayIndexing={index}
                                dataObj={data}
                                haveDependentSubFields
                                onChange={(updatedData) => handleAddressChangeWithType(address?.type, updatedData)}
                                customProp={iPlusFields?.filter(field => field?.parentField === "addressList[]")}
                                showError={hasError}
                                requiredOverride
                                onError={(errorMsg, streetNameLabel) => {
                                    const label = streetNameLabel || `addressList[${index}].street`;
                                    handleErrors(label, errorMsg);
                                }}
                                isForEDD={isForEDD}
                            />
                        </Row>

                        <Row>
                            <WrapperField
                                disabled={asLabel}
                                jsonField={contextualAddressField(
                                    cyclosFields,
                                    "addressList[].buildingNumber",
                                    ADDRESS_LABELS[address?.type] || "Address"
                                )}
                                arrayIndexing={index}
                                dataObj={data}
                                onChange={(updatedData) => handleAddressChangeWithType(address?.type, updatedData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].buildingNumber`, errorMsg)}
                                isForEDD={isForEDD}
                            />
                        </Row>

                        <Row>
                            <WrapperField
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].city")}
                                arrayIndexing={index}
                                dataObj={data}
                                className="col-sm-6 col-md-3"
                                onChange={(updatedData) => handleAddressChangeWithType(address?.type, updatedData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].city`, errorMsg)}
                                isForEDD={isForEDD}
                            />

                            <WrapperField
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].state")}
                                arrayIndexing={index}
                                dataObj={data}
                                className="col-sm-6 col-md-3"
                                onChange={(updatedData) => handleAddressChangeWithType(address?.type, updatedData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].state`, errorMsg)}
                                isForEDD={isForEDD}
                            />

                            {!address?.hidePinCode && (
                                <WrapperField
                                    disabled={asLabel}
                                    jsonField={compactAddressField(cyclosFields, "addressList[].pinCode")}
                                    arrayIndexing={index}
                                    dataObj={data}
                                    className="col-sm-6 col-md-3"
                                    onChange={(updatedData) => handleAddressChangeWithType(address?.type, updatedData)}
                                    showError={hasError}
                                    onError={(errorMsg) => handleErrors(`addressList[${index}].pinCode`, errorMsg)}
                                    isForEDD={isForEDD}
                                />
                            )}

                            <WrapperField
                                disabled={asLabel}
                                jsonField={compactAddressField(cyclosFields, "addressList[].country")}
                                arrayIndexing={index}
                                dataObj={data}
                                className="col-sm-6 col-md-3 mt-3"
                                onChange={(updatedData) => handleAddressChangeWithType(address?.type, updatedData)}
                                showError={hasError}
                                onError={(errorMsg) => handleErrors(`addressList[${index}].country`, errorMsg)}
                                isForEDD={isForEDD}
                            />

                            {address?.hidePinCode && (
                                <div className="col-sm-6 col-md-3" aria-hidden="true" />
                            )}
                        </Row>
                    </div>
                );
            })}

            {/* Rest of the fields */}
            <WrapperField
                disabled={asLabel || !canEditBusinessRegistrationCountry}
                asDisplay={!canEditBusinessRegistrationCountry}
                jsonField={cyclosFields["Business_Registration_Country"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={canEditBusinessRegistrationCountry ? hasError : false}
                onError={canEditBusinessRegistrationCountry ? (errorMsg) => handleErrors('Business_Registration_Country', errorMsg) : noop}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel || !canEditEIN}
                asDisplay={!canEditEIN}
                jsonField={cyclosFields["EIN"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={canEditEIN ? hasError : false}
                onError={canEditEIN ? (errorMsg) => handleErrors('EIN', errorMsg) : noop}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel || !canEditBusinessTaxId}
                asDisplay={!canEditBusinessTaxId}
                jsonField={cyclosFields["Business_Tax_ID"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={canEditBusinessTaxId ? hasError : false}
                onError={canEditBusinessTaxId ? (errorMsg) => handleErrors('Business_Tax_ID', errorMsg) : noop}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel || !canEditBusinessMobilePhone}
                asDisplay={!canEditBusinessMobilePhone}
                jsonField={cyclosFields["mobilePhone"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={canEditBusinessMobilePhone ? hasError : false}
                onError={canEditBusinessMobilePhone ? (errorMsg) => handleErrors('mobilePhone', errorMsg) : noop}
                isForEDD={isForEDD}
                customProp={iPlusFields?.filter(field => field?.field === "mobilePhone")?.[0]}
            />

            <WrapperField
                disabled={asLabel || !canEditBusinessLandlinePhone}
                asDisplay={!canEditBusinessLandlinePhone}
                jsonField={cyclosFields["landlinePhone"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={canEditBusinessLandlinePhone ? hasError : false}
                onError={canEditBusinessLandlinePhone ? (errorMsg) => handleErrors('landlinePhone', errorMsg) : noop}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["Entity_Type"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('Entity_Type', errorMsg)}
                isForEDD={isForEDD}
            />

            {/* Continue with remaining fields... */}
            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["Business_Registration_Number"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('Business_Registration_Number', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["Business_Formation_Date"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('Business_Formation_Date', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["inOperation"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('inOperation', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["operationStartDate"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('operationStartDate', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["operateEstDate"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('operateEstDate', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["Business_Industry_Type"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('Business_Industry_Type', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["website"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('website', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["url"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('url', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["engage"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('engage', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["empCount"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('empCount', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["Bank_Account_Another_Financial_Institution"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('Bank_Account_Another_Financial_Institution', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["additionalBankAccountInstName"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('additionalBankAccountInstName', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["penalty"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('penalty', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["penaltyDetails"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('penaltyDetails', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["litigation"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('litigation', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["litigationDetails"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('litigationDetails', errorMsg)}
                isForEDD={isForEDD}
            />

            <WrapperField
                disabled={asLabel}
                jsonField={cyclosFields["BUS_Source_Of_Funds"]}
                dataObj={data}
                onChange={handleUpdateFormData}
                showError={hasError}
                onError={(errorMsg) => handleErrors('BUS_Source_Of_Funds', errorMsg)}
                isForEDD={isForEDD}
            />


            <WrapperField
                asDisplay={asLabel || !canEditEntities}
                disabled={asLabel}
                jsonField={cyclosFields["hasEntity"]}
                dataObj={data}
                onChange={(updatedValue) => {
                    if (!canEditEntities) return;
                    let next = updatedValue;
                    // If user indicates there are no entities, clear the list so removals are tracked
                    // (diffMap.entities) and visible in both UI + manual update email.
                    if (next?.hasEntity === false) {
                        if (isForEDD) {
                            next = ensureArrayDiffBaseline(next, 'entities');
                        }
                        next = setValue(next, 'entities', [], isForEDD);
                    } else if (next?.hasEntity === true && (!Array.isArray(next?.entities) || next.entities.length === 0)) {
                        if (isForEDD) {
                            next = ensureArrayDiffBaseline(next, 'entities');
                        }
                        next = setValue(next, 'entities', [{ _isNew: true, _localId: buildLocalId() }], isForEDD);
                    }
                    handleUpdateFormData(next);
                }}
                showError={hasError}
                onError={(errorMsg) => handleErrors('hasEntity', errorMsg)}
                isForEDD={isForEDD}
            />

            {/* Entities Section */}
            {data?.hasEntity && (
                <div>
                    {(data?.entities || [{}]).map((_, i) => {
                        const entityKey = buildEntityRenderKey(data?.entities?.[i], i);
                        return (
                        <div className='box-tab mx-0' key={entityKey}>
                            <div className='d-flex align-items-center justify-content-between'>
                                <div style={{ fontWeight: 'bold', fontSize: 18 }}>
                                    Entity {i + 1}
                                    {showChangeTracking && addedEntityIndices?.has(i) && <span className='ms-2' style={ADDED_BADGE_STYLE}>Added</span>}
                                </div>
                                <div className='me-2' onClick={canEditEntities ? () => removeEntity(i) : undefined}>
                                    {(data?.entities?.length > 1 && canEditEntities) && <FaTimes size={22} color='red' />}
                                </div>
                            </div>
                            <Entity
                                index={i}
                                asLabel={asLabel}
                                data={data}
                                handleChange={handleUpdateFormData}
                                hasError={hasError}
                                cyclosFields={cyclosFields}
                                setErrors={setErrors}
                                errors={errors}
                                iPlusFields={iPlusFields}
                                setData={setData}
                                isForEDD={showChangeTracking && !(data?.entities?.[i]?._isNew || addedEntityIndices?.has(i))}
                                isEditable={canEditEntities}
                            />
                        </div>
                        );
                    })}
                    {canEditEntities && <BTN onClick={AddEntity} label="Add Entity" />}
                </div>
            )}

            {showChangeTracking && removedEntities.length > 0 && (
                <div className='mt-3'>
                    <ModernDisclosure
                        tone="removed"
                        title={`Removed Entities (${removedEntities.length})`}
                        subtitle={buildCompactListSummary(
                            removedEntities,
                            (item, idx) => {
                                const name = item?.name ? String(item.name).trim() : '';
                                const rel = item?.relations ? `(${item.relations})` : '';
                                const label = [name, rel].filter(Boolean).join(' ').trim();
                                return [label || `Entity #${idx + 1}`].filter(Boolean).join(' • ');
                            },
                            2
                        )}
                        chips={[{ label: 'Removed', variant: 'removed' }]}
                        defaultOpen
                    >
                        {removedEntities.map((entity, idx) => {
                            const identity = buildEntityIdentity(entity, idx);
                            const canReAdd = !asLabel && permissions.allowEntities;
                            // Entity fields in the form json are guarded by `showIf: hasEntity === true`.
                            // If the user toggles `hasEntity` to "No" (which is how entities get removed),
                            // we still want removed entity cards to render the full details.
                            const entityData = { ...(data || {}), hasEntity: true, entities: [entity] };
                            const headerLabel = entity?.name
                                ? `Entity — ${entity.name}`
                                : `Entity #${idx + 1}`;
                            return (
                                <div className='box-tab mx-0' key={`removed-entity-card-${identity}-${idx}`}>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <div>
                                            <div style={{ fontWeight: 'bold', fontSize: 18 }}>
                                                {headerLabel}
                                                <span className='ms-2' style={REMOVED_BADGE_STYLE}>Removed</span>
                                            </div>
                                        </div>
                                        {canReAdd ? (
                                            <button
                                                type="button"
                                                className="modernDisclosure__actionBtn"
                                                onClick={() => reAddRemovedEntity(entity)}
                                            >
                                                Re-add
                                            </button>
                                        ) : null}
                                    </div>
                                    <Entity
                                        index={0}
                                        asLabel
                                        data={entityData}
                                        handleChange={() => { }}
                                        hasError={false}
                                        cyclosFields={cyclosFields}
                                        setErrors={() => { }}
                                        isForEDD={false}
                                        isEditable={false}
                                    />
                                </div>
                            );
                        })}
                    </ModernDisclosure>
                </div>
            )}

            {/* ======================= Users ======================= */}
            <div>
                <h2>Officers or Authorized Account Users</h2>
                In this section, list all directors and officers in your organization. Officers include Chief Executive Officer, Chief Financial Officer, Chief Operating Officer, Director or other persons that exert control over the business. You may also list persons who are not controllers but whom you request have informational access to your account for operational purposes.
                <h2 className='mt-4'>
                    {permissions.allowAdditionalIndividuals
                        ? 'Enter user information, click Add User below to add more users'
                        : 'Review your existing user information below'}
                </h2>
            </div>

            <div>
                {(data?.individuals || [{}]).map((_, i) => {
                    const isEditableUser = canEditIndividual(i);
                    const canRemoveUser =
                        !asLabel &&
                        permissions.allowAdditionalIndividuals &&
                        isEditableUser &&
                        i !== effectivePrimaryIndex &&
                        (data?.individuals?.length > 1);
                    const individualKey = buildIndividualRenderKey(data?.individuals?.[i], i);

                return (
                    <div className='box-tab' key={individualKey}>
                        <div className='d-flex align-items-center justify-content-between'>
                                <div style={{ fontWeight: 'bold', fontSize: 18 }}>
                                    {data?.individuals?.[i]?.isPrimaryUser ? `Primary User` : `User ${i + 1}`}
                                    {supportsControlPersonFlow && data?.individuals?.[i]?.isControlPerson && <span className='ms-2' style={CONTROL_PERSON_BADGE_STYLE}>Control Prong</span>}
                                    {showChangeTracking && addedIndividualIndices?.has(i) && <span className='ms-2' style={ADDED_BADGE_STYLE}>Added</span>}
                                </div>
                            <div className='me-2' onClick={canRemoveUser ? () => removeUser(i) : undefined}>
                                {canRemoveUser && <FaTimes size={22} color='red' />}
                            </div>
                        </div>
                        <User
                            index={i}
                            asLabel={asLabel}
                            data={data}
                            handleChange={handleUpdateFormData}
                            hasError={hasError}
                            cyclosFields={cyclosFields}
                            setErrors={setErrors}
                            errors={errors}
                            iPlusFields={iPlusFields}
                            isFieldEditable={evaluateFieldEditable}
                            setData={(updatedData) => {
                                handleChange(updatedData);
                            }}
                            isForEDD={showChangeTracking && !(data?.individuals?.[i]?._isNew || addedIndividualIndices?.has(i))}
                            applicationForm={applicationForm}
                            isEditable={isEditableUser}
                            readOnlyMessage={undefined}
                            />

                        </div>
                    );
                })}
            </div>

            {!asLabel && permissions.allowAdditionalIndividuals && (
                <div className='d-flex flex-wrap gap-2'>
                    <BTN
                        onClick={AddUser}
                        label="Add Officer/Authorized User"
                        disabled={data?.individuals?.filter(ind => ind?.isAuthorizedUser)?.length >= effectiveMaxAuthorizedUsers}
                    />
                    <BTN onClick={AddShareholder} label="Add Beneficial Owner/Shareholder" />
                    {supportsControlPersonFlow ? <BTN onClick={AddControlPerson} label="Add Control Prong" /> : null}
                    <BTN onClick={AddIndependentDirector} label="Add Director/Manager" />
                </div>
            )}

            {showChangeTracking && removedIndividuals.length > 0 && (
                <div className='mt-3'>
                    <ModernDisclosure
                        tone="removed"
                        title={`Removed Users (${removedIndividuals.length})`}
                        subtitle={buildCompactListSummary(
                            removedIndividuals,
                            (item, idx) => {
                                const name = [item?.firstName, item?.lastName].filter(Boolean).join(' ').trim();
                                const base = (name && item?.email)
                                    ? `${name} (${item.email})`
                                    : (name || item?.email || `User #${idx + 1}`);
                                return [base].filter(Boolean).join(' • ');
                            },
                            2
                        )}
                        chips={[{ label: 'Removed', variant: 'removed' }]}
                        defaultOpen
                    >
                        {removedIndividuals.map((individual, idx) => {
                            const identity = buildIndividualIdentity(individual, idx);
                            const canReAdd = !asLabel && permissions.allowAdditionalIndividuals;
                            const userData = { individuals: [individual] };
                            const headerLabel = buildIndividualTitle(individual, idx).replace(/^Removed\s+User\s+—\s*/i, 'User — ');
                            return (
                                <div className='box-tab' key={`removed-user-card-${identity}-${idx}`}>
                                    <div className='d-flex align-items-center justify-content-between'>
                                        <div>
                                            <div style={{ fontWeight: 'bold', fontSize: 18 }}>
                                                {headerLabel}
                                                {supportsControlPersonFlow && individual?.isControlPerson ? <span className='ms-2' style={CONTROL_PERSON_BADGE_STYLE}>Control Prong</span> : null}
                                                <span className='ms-2' style={REMOVED_BADGE_STYLE}>Removed</span>
                                            </div>
                                        </div>
                                        {canReAdd ? (
                                            <button
                                                type="button"
                                                className="modernDisclosure__actionBtn"
                                                onClick={() => reAddRemovedUser(individual)}
                                            >
                                                Re-add
                                            </button>
                                        ) : null}
                                    </div>
                                    <User
                                        index={0}
                                        asLabel
                                        data={userData}
                                        handleChange={() => { }}
                                        hasError={false}
                                        cyclosFields={cyclosFields}
                                        setErrors={() => { }}
                                        iPlusFields={iPlusFields}
                                        isFieldEditable={() => false}
                                        isForEDD={false}
                                        applicationForm={applicationForm}
                                        isEditable={false}
                                        readOnlyMessage={undefined}
                                    />
                                </div>
                            );
                        })}
                    </ModernDisclosure>
                </div>
            )}



            {!hideButtons && (
                <div className='mt-4'>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        onBackClick={() => handlePrev?.()}
                        step={pageNum}
                        showPageNum={false}
                        pageNum={pageNum}
                        asLabel={false}
                        loading={loading}
                    />
                </div>
            )}
        </div>
    );
};

export default observer(BusinessInformation);
